import { createFileRoute, redirect } from '@tanstack/react-router';
import { NumberInput, toNumberValue } from '@/components/molecules/NumberInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Switch } from '@/components/atoms/switch';
import {
  ContractSettingsTabShell,
  FieldRow,
} from '@/components/settings/contracts/ContractSettingsTabShell';

/**
 * Spec §14.5 (and §12.11) — Reviews tab. Default ReviewRequestRule strategy
 * per billing mode, cooldown, two-step funnel, public-review threshold,
 * recovery owner, adaptive routing, fatigue gates.
 */

export const Route = createFileRoute('/_dashboard/dashboard/settings/contracts/reviews')({
  beforeLoad: () => {
    throw redirect({ to: '/dashboard/settings/reviews' });
  },
  component: ContractReviewsPage,
});

const STRATEGY_OPTIONS = [
  { value: 'every_visit', label: 'Every visit' },
  { value: 'first_visit_only', label: 'First visit only' },
  { value: 'nth_visit', label: 'Nth visit' },
  { value: 'cadence', label: 'Cadence (every N visits / days)' },
  { value: 'milestone', label: 'Milestone (1st, 4th, 12th …)' },
  { value: 'triggered_only', label: 'Manual trigger only' },
  { value: 'disabled', label: 'Disabled' },
];

const RECOVERY_OWNERS = [
  { value: 'account_owner', label: 'Account owner' },
  { value: 'tenant_admin', label: 'Tenant admin' },
  { value: 'escalation_queue', label: 'Escalation queue' },
];

interface StrategyMap {
  per_visit?: string;
  subscription?: string;
  prepaid?: string;
}

function ContractReviewsPage() {
  return (
    <ContractSettingsTabShell
      tab="reviews"
      title="Reviews & Reputation"
      render={(draft, update) => <ContractReviewsFields draft={draft} update={update} />}
    />
  );
}

export function ContractReviewsFields({
  draft,
  update,
}: {
  draft: Record<string, unknown>;
  update: (key: string, value: unknown) => void;
}) {
  const strategyMap = (draft.defaultStrategyByBillingMode ?? {}) as StrategyMap;
  const setStrategy = (mode: keyof StrategyMap, value: string) => {
    update('defaultStrategyByBillingMode', {
      ...strategyMap,
      [mode]: value,
    });
  };

  return (
    <>
      <FieldRow label="Default strategy: per-visit billing">
        <Select
          value={String(strategyMap.per_visit ?? 'every_visit')}
          onValueChange={(v) => setStrategy('per_visit', v)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STRATEGY_OPTIONS.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldRow>

      <FieldRow label="Default strategy: subscription billing">
        <Select
          value={String(strategyMap.subscription ?? 'cadence')}
          onValueChange={(v) => setStrategy('subscription', v)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STRATEGY_OPTIONS.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldRow>

      <FieldRow label="Default strategy: prepaid billing">
        <Select
          value={String(strategyMap.prepaid ?? 'milestone')}
          onValueChange={(v) => setStrategy('prepaid', v)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STRATEGY_OPTIONS.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldRow>

      <FieldRow label="Cooldown between requests (days)">
        <NumberInput
          min={0}
          max={365}
          value={toNumberValue(draft.cooldownDays)}
          allowDecimal={false}
          placeholder="60"
          onChange={(next) => update('cooldownDays', next)}
        />
      </FieldRow>

      {/* Same rename as settings/reviews.tsx — post-de-gate this number only
          decides whether we extend another invitation, never where a customer
          who already rated is allowed to go. */}
      <FieldRow label="Ask again only if they last rated">
        <NumberInput
          min={1}
          max={5}
          value={toNumberValue(draft.publicReviewThreshold)}
          allowDecimal={false}
          placeholder="4"
          onChange={(next) => update('publicReviewThreshold', next)}
        />
      </FieldRow>

      <FieldRow label="Negative-rating recovery owner">
        <Select
          value={String(draft.negativeRatingRecoveryOwner ?? 'account_owner')}
          onValueChange={(v) => update('negativeRatingRecoveryOwner', v)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RECOVERY_OWNERS.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldRow>

      <FieldRow label="Balance reviews across platforms">
        <Switch
          checked={Boolean(draft.adaptivePlatformRouting ?? true)}
          onCheckedChange={(v) => update('adaptivePlatformRouting', v)}
        />
      </FieldRow>

      <FieldRow label="Skip if customer has open dispute">
        <Switch checked disabled onCheckedChange={() => undefined} />
      </FieldRow>

      <FieldRow label="Skip if unpaid invoice older than (days)">
        <NumberInput
          min={0}
          max={365}
          value={toNumberValue(draft.skipIfUnpaidInvoiceDays)}
          allowDecimal={false}
          placeholder="14"
          onChange={(next) => update('skipIfUnpaidInvoiceDays', next)}
        />
      </FieldRow>

      <FieldRow label="Tech reputation tracking">
        <Switch
          checked={Boolean(draft.techReputationTracking ?? true)}
          onCheckedChange={(v) => update('techReputationTracking', v)}
        />
      </FieldRow>
    </>
  );
}
